<script setup lang="ts">
import { computed, ref } from 'vue';
import { onKeyStroke } from '@vueuse/core';
import { useIntervalFn } from '@vueuse/core';

const props = defineProps({
  items: {
    type: Array,
    required: false,
    default: () => []
  },
  disable: {
    type: Boolean,
    default: false
  }
});

const { userData } = useAuth();
const dealerDenyList = userData.value?.dealers[0].denyListProductClassifications ?? [];
// const dealerDenyList = ['Wholesale'];

const injectedComponents = inject('siteWideComponents', []);
const filteredComponents = ref(props.items.length > 0 ? props.items : injectedComponents.items);
const activeSlide = ref(0);
const direction = ref<'right' | 'left'>('right');

// filter by 'startDate' and 'endDate' and only show if in range.
const now = new Date();
filteredComponents.value = filteredComponents.value.filter(item => {
  // if startDate or endDate are null/empty we must consider this 'forever'
  if (!item.content.properties.startDate && !item.content.properties.endDate) {
    return true;
  }
  // if no endDate, it must be visible from the startDate till forever
  if (!item.content.properties.endDate) {
    const startDate = new Date(item.content.properties.startDate);
    return startDate <= now;
  }
  const startDate = new Date(item.content.properties.startDate);
  const endDate = new Date(item.content.properties.endDate);
  return startDate <= now && endDate >= now;
});

filteredComponents.value = filteredComponents.value.filter(item => {
  const classifications = item?.settings?.properties?.denyListProductClassifications;

  // Ensure classifications is an array
  if (Array.isArray(classifications)) {
    // Check if there is no overlap with dealerDenyList
    return classifications.every(classification => !dealerDenyList.includes(classification));
  }
  // If classifications is not an array, include the item (if that's the intent)
  return true;
});

function updateActiveSlide(newDirection: 'right' | 'left') {
  direction.value = newDirection;
  const lastIndex = filteredComponents.value.length - 1;

  if (newDirection === 'right') {
    activeSlide.value = activeSlide.value === lastIndex ? 0 : activeSlide.value + 1;
  } else {
    activeSlide.value = activeSlide.value === 0 ? lastIndex : activeSlide.value - 1;
  }
}

useIntervalFn(
  () => {
    updateActiveSlide(direction.value);
  },
  7500,
  { immediate: true }
);

onKeyStroke('ArrowRight', e => {
  e.preventDefault();
  updateActiveSlide('right');
});

onKeyStroke('ArrowLeft', e => {
  e.preventDefault();
  updateActiveSlide('left');
});

// Dynamically compute transition classes
const TRANSITION_BASE = {
  enterActiveClass: 'transition-all duration-500 ease-out',
  leaveActiveClass: 'transition-all duration-500 ease-in'
};

const getTransitionClassByDirection = (direction: 'right' | 'left') => {
  return direction === 'right'
    ? {
        enterFromClass: 'translate-x-[100%]',
        enterToClass: 'translate-x-0',
        leaveFromClass: 'translate-x-0',
        leaveToClass: 'opacity-0 -translate-x-[100%]'
      }
    : {
        enterFromClass: '-translate-x-[100%]',
        enterToClass: 'translate-x-0',
        leaveFromClass: 'translate-x-0',
        leaveToClass: 'opacity-0 translate-x-[90%]'
      };
};

const getTransitionClasses = computed(() => ({
  ...TRANSITION_BASE,
  ...getTransitionClassByDirection(direction.value)
}));
</script>

<template>
  <div class="bg-may-green text-white" v-if="filteredComponents.length > 0">
    <transition
      :enter-active-class="getTransitionClasses.enterActiveClass"
      :enter-from-class="getTransitionClasses.enterFromClass"
      :enter-to-class="getTransitionClasses.enterToClass"
      :leave-active-class="getTransitionClasses.leaveActiveClass"
      :leave-from-class="getTransitionClasses.leaveFromClass"
      :leave-to-class="getTransitionClasses.leaveToClass"
      mode="out-in"
    >
      <SiteWideMessagingItem
        v-bind="filteredComponents[activeSlide]?.content?.properties"
        :key="filteredComponents[activeSlide]?.content.id"
      />
    </transition>
  </div>
</template>

<style scoped></style>
